import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Chip,
  ContentEditor,
  Select,
  SlideShow,
  theme,
} from "kosmos-ui";

import { useGetProductQuery } from "api/graphql/queries/products/hooks";
import { useLayout } from "app/context/LayoutContext/hooks";
import { formatNumber } from "app/helpers/general";
import { getProductDiscountToRender } from "app/helpers/products";

import { getProductPrices } from "./helpers";
import {
  ProductContainer,
  ProductContentContainer,
  ProductImg,
  ProductImgContainer,
  ProductImgsThumbContainer,
  ProductImgThumb,
  ProductImgThumbContainer,
  ProductInfoContainer,
  ProductNameContainer,
  ProductPriceContainer,
  ProductPrice,
  productVariantSelectStyles,
} from "./styles";

export default function Product() {
  const [selectedImg, setSelectedImg] = useState<string | undefined>(undefined);
  const [selectedVariant, setSelectedVariant] = useState<
    { value: string; label: string } | undefined
  >(undefined);

  const { _id } = useParams<WithId>();

  const { data: productData, loading: loadingProductData } = useGetProductQuery(
    { variables: { _id } }
  );

  const { mobile, topbarHeight, announcementBarHeight } = useLayout();

  const product = productData?.getProduct;

  useEffect(() => {
    if (product) setSelectedImg(product.images && product.images[0].url);
  }, [product]);

  const variantsOptions =
    product?.variants &&
    product.variants.length > 0 &&
    product.variants.map((variant) => {
      const { value, label } = variant.mixin.reduce(
        (acc, mixin, index) => {
          return {
            value: variant._id,
            label: index !== 0 ? `${acc.label}/ ${mixin.name}` : mixin.name,
          };
        },
        { value: "", label: "" }
      );

      return { value, label };
    });

  const stock =
    product?.variants && product.variants.length > 0
      ? (
          product.variants.find(
            (variant) => variant._id === selectedVariant?.value
          ) || { stock: 1 }
        ).stock
      : product?.stock;

  const { price, discountPrice } = getProductPrices({
    product,
    selectedVariant,
  });

  const headerHeight = topbarHeight + announcementBarHeight;

  return (
    <ProductContainer>
      {loadingProductData ? (
        <p>Cargando producto...</p>
      ) : (
        <ProductContentContainer headerHeight={headerHeight}>
          {product?.images && product.images.length > 1 && !mobile && (
            <ProductImgsThumbContainer>
              {product.images.map((productImage, index) => (
                <ProductImgThumbContainer
                  key={`${productImage}-${index}`}
                  onClick={() => setSelectedImg(productImage.url)}
                >
                  <ProductImgThumb src={productImage.url} />
                </ProductImgThumbContainer>
              ))}
            </ProductImgsThumbContainer>
          )}

          <ProductImgContainer>
            {mobile ? (
              <SlideShow
                slides={product?.images?.map((image) => image.url || "") || []}
                style={{ slide: { height: "40vh", objectFit: "cover" } }}
              />
            ) : (
              <ProductImg src={selectedImg} />
            )}
          </ProductImgContainer>

          <ProductInfoContainer>
            <Typography as="h2" variant="subtitle1">
              {product?.category?.name}
            </Typography>

            <ProductNameContainer>
              <Typography as="h2" variant="title3" style={{ margin: 0 }}>
                {product?.name}
              </Typography>

              {stock === 0 && (
                <Chip type="secondary" style={{ marginLeft: 8 }}>
                  Agotado
                </Chip>
              )}
            </ProductNameContainer>

            {price && (
              <ProductPriceContainer>
                {!!discountPrice && (
                  <ProductPrice
                    isOriginalPrice={false}
                    discountPrice={discountPrice}
                  >
                    ${formatNumber(discountPrice)}
                  </ProductPrice>
                )}

                <ProductPrice
                  isOriginalPrice={true}
                  discountPrice={discountPrice}
                >
                  ${formatNumber(price)}
                </ProductPrice>

                {!!discountPrice && (
                  <Chip type="danger">
                    {getProductDiscountToRender(price, discountPrice)}
                  </Chip>
                )}
              </ProductPriceContainer>
            )}

            {variantsOptions && (
              <Select
                options={variantsOptions}
                value={selectedVariant}
                onChange={(selectedVariant: any) =>
                  setSelectedVariant(selectedVariant)
                }
                styles={productVariantSelectStyles}
                placeholder="Selecciona una variante"
              />
            )}

            <ContentEditor
              value={product?.description || ""}
              readOnly
              style={{
                editor: { border: "none" },
                root: {
                  border: "none",
                  padding: 0,
                  zIndex: 0,
                  backgroundColor: theme.colorsPalette.black.darker,
                  color: theme.colorsPalette.gray.light,
                },
              }}
            />

            <Button
              variant="primary"
              as="a"
              href={`${product?.company.domain}/product/${product?._id}`}
              target="_blank"
              style={{ marginTop: 32 }}
            >
              Ver en tienda
            </Button>
          </ProductInfoContainer>
        </ProductContentContainer>
      )}
    </ProductContainer>
  );
}
